<template>
  <div :key="componentKey">
    <DetailToolbar
      v-if="!inModal"
      :isNew="isNew"
      :editMode="editMode"
      :isAnyFieldDirty="isAnyFieldDirty"
      :onGoToList="goToList"
      :onToggleEditMode="toggleEditMode"
      :headerText="document?.name"
      :showDeleteButton="true"
      :onConfirmDelete="confirmDelete"
      :confirmationMessage="getConfirmationMessage()"
    >
      <template v-if="isNew" #end>
        <ButtonGroup>
          <Button
            v-if="!hasFilesToUpload"
            v-tooltip="{ value: t('titletags.chooseFiles') }"
            icon="pi pi-images"
            :label="t('labels.chooseFiles')"
            @click="handleChoose"
          />
          <Button
            v-if="!!hasFilesToUpload"
            v-tooltip="{ value: t('titletags.reset') }"
            icon="pi pi-times"
            severity="danger"
            @click="handleClear"
          />
          <Button
            v-if="!!hasFilesToUpload"
            v-tooltip="{ value: t('titletags.uploadFiles') }"
            icon="pi pi-cloud-upload"
            :label="t('labels.uploadFiles')"
            @click="handleUpload"
          />
        </ButtonGroup>
      </template>
    </DetailToolbar>
    <DocumentFileUpload v-if="isNew" ref="uploadComponentRef" hideButtons />
    <div v-else>
      <SectionWrapper :class="editMode ? 'editmode' : 'viewmode'">
        <form>
          <div class="flex">
            <FormInputText
              v-model="name"
              name="name"
              :isDirty="handleDirtyState('name')"
              :initialValue="editData.name"
              :disabled="!editMode"
            />
          </div>
          <div class="flex">
            <FormTextArea
              v-model="comment"
              name="comment"
              :isDirty="handleDirtyState('comment')"
              :initialValue="editData.comment"
              :disabled="!editMode"
            />
          </div>
          <div class="flex viewmode">
            <FormCheckbox
              v-model="stamped"
              name="stamped"
              :isDirty="handleDirtyState('stamped')"
              :initialValue="editData.stamped"
              :disabled="true"
            />
            <FormInputText
              v-model="mimeType"
              name="mimeType"
              :isDirty="handleDirtyState('mimeType')"
              :initialValue="editData.mimeType"
              :disabled="true"
            />
            <FormInputText
              v-model="size"
              name="size"
              :isDirty="handleDirtyState('size')"
              :initialValue="editData.size"
              :disabled="true"
            />
            <FormDatePicker
              v-model="createdAt"
              name="createdAt"
              :isDirty="handleDirtyState('createdAt')"
              :initialValue="editData.createdAt"
              :disabled="true"
            />
            <FormDatePicker
              v-model="modifiedAt"
              name="modifiedAt"
              :isDirty="handleDirtyState('modifiedAt')"
              :initialValue="editData.modifiedAt"
              :disabled="true"
            />
          </div>
          <Badge
            v-if="!!document"
            v-tooltip="{
              value: document.isAttachment
                ? t('messages.documentDetailClassificationAttachment')
                : t('messages.documentDetailClassificationUploadedDocument'),
              fitContent: true,
              showDelay: 300,
            }"
            :severity="getBatchSeverity()"
            class="ml-auto mb-4"
            :value="document.isAttachment ? t('labels.attachment') : t('labels.uploadedDocument')"
          />
          <div
            v-for="(classification, index) in classifications"
            :key="classification.key"
            class="mb-4 flex items-center"
          >
            <TicketSelection
              :model="classification.value"
              :initialData="{
                pointOfSaleId: initialClassifications[index]?.pointOfSaleId ?? undefined,
                documentType: initialClassifications[index]?.documentType ?? undefined,
                ticketId: initialClassifications[index]?.ticketId ?? undefined,
              }"
              :namePrefix="`classifications.${index}`"
              :formPrefix="`classifications.${index}`"
              :fetchPointOfSalesData="getClassificationFetchPointOfSales(index)"
              :fetchTicketsData="fetchTicketsForLazySelect"
              :fetchSinglePointOfSale="fetchSinglePointOfSaleById"
              :fetchSingleTicket="fetchSingleTicketById"
              :editMode="editMode && classifications.length === 1"
              @update:pointOfSaleId="(val) => (classification.value.pointOfSaleId = val)"
              @update:documentType="(val) => (classification.value.documentType = val)"
              @update:ticketId="(val) => (classification.value.ticketId = val)"
              @dirty="(isDirty) => handleDirtyState(`classifications.${index}`)(isDirty)"
            />
            <i
              v-if="editMode && classifications.length > 1"
              class="pi pi-times text-red-500 cursor-pointer"
              @click="handleRemoveRelation(index)"
            ></i>
          </div>

          <DetailFormfooter
            :isNew="isNew"
            :editMode="editMode"
            :isAnyFieldDirty="isAnyFieldDirty"
            :onGoToList="goToList"
            :onToggleEditMode="toggleEditMode"
            :onSubmit="submitForm"
            :onConfirmResetChanges="confirmResetChanges"
          />
        </form>
      </SectionWrapper>
      <SectionWrapper v-if="document?.documentId" class="new p-0">
        <FileViewer
          :uuid="document.documentId"
          :mime="document.mimeType ?? 'application/pdf'"
          :filename="document.name"
        />
      </SectionWrapper>
    </div>
    <Confirm ref="confirmDialog" />

    <TrackRecordingTable v-if="id && !inModal" :id="id" />
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch, watchEffect } from 'vue';
import { useField, useFieldArray, useForm } from 'vee-validate';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';

import Confirm from '@/components/confirm/Confirm.vue';
import {
  DetailNavigationInlinePropsDefaults,
  useDetailNavigation,
  type UseDetailNavigationInlineProps,
} from '@/components/details/hooks/useDetailNavigation';
import { useFormDirtyState } from '@/components/forms/hooks/useFormDirtyState';
import TicketSelection from '@/views/classification/components/TicketSelection.vue';
import { useClassificationLookups } from '@/views/classification/hooks/useClassificationLookups';
import { useDetailView } from '@/composables/useDetailView';
import { useHeadlineStore } from '@/stores/headline';

import type { ClassificationType } from '../classification/classificationTypes';

import DocumentFileUpload from './components/DocumentFileUpload.vue';
import { getDocumentSchema } from './configs/documentFormConfig';
import { useDocuments, useDocumentsConverter } from './hooks/useDocuments';
import type { DocumentSimple } from './documentsTypes';

const { t } = useI18n();
const route = useRoute();
const headlineStore = useHeadlineStore();
const { id, isNew, editMode, goToList, toggleEditMode } = useDetailNavigation({
  uuidFromProp: computed(() => props.uuid),
});
const confirmDialog = ref<InstanceType<typeof Confirm> | null>(null);

const props = withDefaults(
  defineProps<UseDetailNavigationInlineProps>(),
  DetailNavigationInlinePropsDefaults,
);

const { isAnyFieldDirty, handleDirtyState } = useFormDirtyState();
const { fetchDocument, updateDocument, deleteDocument, deleteDocumentRelation } = useDocuments();
const {
  convertDocumentDtoToDocumentSimple,
  convertDocumentSimpleToUpdateDocumentDto,
  extractClassifications,
} = useDocumentsConverter();

const document = ref<DocumentSimple>();
const editData = ref({ ...document.value });
const initialClassifications = ref<ClassificationType[]>([]);
const uploadComponentRef = ref<InstanceType<typeof DocumentFileUpload>>();

const handleChoose = () => {
  uploadComponentRef.value?.chooseFiles();
};

const handleUpload = () => {
  uploadComponentRef.value?.startUpload();
};

const handleClear = () => {
  uploadComponentRef.value?.clearFiles();
};

type DocumentFormValues = DocumentSimple & {
  classifications?: ClassificationType[];
};

const { handleSubmit, setValues, setFieldError } = useForm<DocumentFormValues>({
  validationSchema: getDocumentSchema(t),
});
const submitForm = handleSubmit(onSubmit);

const { value: name } = useField<string>('name');
const { value: createdAt } = useField<string>('createdAt');
const { value: modifiedAt } = useField<string>('modifiedAt');
const { value: mimeType } = useField<string>('mimeType');
const { value: size } = useField<string>('size');
const { value: stamped } = useField<boolean>('stamped');
const { value: comment } = useField<string>('comment');

const fieldArray = useFieldArray<ClassificationType>('classifications');
const { fields: classifications } = fieldArray;

const {
  createFetchPointOfSalesForLazySelect,
  fetchTicketsForLazySelect,
  fetchSinglePointOfSaleById,
  fetchSingleTicketById,
} = useClassificationLookups();

// Cache of fetch functions per ticketId to avoid recreating on every render
const fetchFunctionsCache = new Map<
  string | undefined,
  ReturnType<typeof createFetchPointOfSalesForLazySelect>
>();

// Get stable fetch function for a specific classification index based on its ticketId
const getClassificationFetchPointOfSales = (index: number) => {
  const ticketId = classifications.value[index]?.value?.ticketId ?? undefined;
  const cacheKey = ticketId ?? 'undefined';

  if (!fetchFunctionsCache.has(cacheKey)) {
    fetchFunctionsCache.set(cacheKey, createFetchPointOfSalesForLazySelect(ticketId));
  }

  return fetchFunctionsCache.get(cacheKey)!;
};

const handleRemoveRelation = async (index: number) => {
  if (!confirmDialog.value) return;

  const isConfirmed = await confirmDialog.value.isConfirmed();

  if (isConfirmed) {
    const classification = classifications.value[index]?.value;
    if (classification) {
      const { ticketId } = classification;
      if (ticketId && document.value) {
        await deleteDocumentRelation(document.value.id, ticketId);
        fieldArray.remove(index);
        await fetchData();
      }
    }
  }
};

const validateClassification = (
  classificationsList: ClassificationType[],
  isAttachment: boolean,
) => {
  let isError = false;
  // Validate each classification
  for (let i = 0; i < classificationsList.length; i++) {
    const classification = classificationsList[i];
    if (!classification) continue;

    const { pointOfSaleId, documentType, ticketId } = classification;
    const hasPointOfSaleId = !!pointOfSaleId;
    const hasDocumentType = !!documentType;
    const hasTicketId = !!ticketId;

    if (hasTicketId && (!hasPointOfSaleId || !hasDocumentType)) {
      if (!hasPointOfSaleId) {
        setFieldError(`classifications[${i}].pointOfSaleId`, t('errors.required'));
      }
      if (!hasDocumentType) {
        setFieldError(`classifications[${i}].documentType`, t('errors.required'));
      }
      isError = true;
    }

    if (isAttachment) {
      const allFilled = hasPointOfSaleId && hasDocumentType && hasTicketId;
      const noneFilled = !hasPointOfSaleId && !hasDocumentType && !hasTicketId;

      if (!allFilled && !noneFilled) {
        if (!hasPointOfSaleId)
          setFieldError(`classifications[${i}].pointOfSaleId`, t('errors.required'));
        if (!hasDocumentType)
          setFieldError(`classifications[${i}].documentType`, t('errors.required'));
        if (!hasTicketId) setFieldError(`classifications[${i}].ticketId`, t('errors.required'));
        isError = true;
      }
    } else {
      const bothFilled = hasPointOfSaleId && hasDocumentType;
      const noneFilled = !hasPointOfSaleId && !hasDocumentType;

      if (!bothFilled && !noneFilled) {
        if (!hasPointOfSaleId)
          setFieldError(`classifications[${i}].pointOfSaleId`, t('errors.required'));
        if (!hasDocumentType)
          setFieldError(`classifications[${i}].documentType`, t('errors.required'));
        isError = true;
      }
    }
  }

  return isError;
};

async function onSubmit(values: DocumentFormValues) {
  const { isAttachment, classifications: classificationsList = [] } = values;

  if (validateClassification(classificationsList, isAttachment)) return;

  // Use first classification for now
  const firstClassification = classificationsList[0] || {};

  const { classifications: _classifications, ...updateData } = values;

  await updateDocument(String(id.value), {
    ...convertDocumentSimpleToUpdateDocumentDto(updateData),
    pointOfSaleId: firstClassification.pointOfSaleId,
    documentType: firstClassification.documentType,
    ticketId: firstClassification.ticketId ?? undefined,
  });

  toggleEditMode();

  const { pointOfSaleId, documentType, ticketId } = firstClassification;
  if (isAttachment && !pointOfSaleId && !documentType && !ticketId) {
    goToList();
  } else {
    await fetchData();
  }
}

function evaluateSeverity(values: unknown[]): 'info' | 'danger' {
  const filled = values.filter((v) => v != null).length;
  return filled === 0 || filled === values.length ? 'info' : 'danger';
}

function getBatchSeverity(): 'info' | 'danger' {
  const doc = document.value;

  if (!classifications.value || classifications.value.length === 0) return 'info';

  const firstClassification = classifications.value[0].value;
  const { pointOfSaleId, documentType, ticketId } = firstClassification;

  if (ticketId) return evaluateSeverity([pointOfSaleId, documentType, ticketId]);
  if (doc?.isAttachment) {
    return evaluateSeverity([pointOfSaleId, documentType, ticketId]);
  } else {
    return evaluateSeverity([pointOfSaleId, documentType]);
  }
}

async function confirmDelete() {
  if (!id.value) return;
  await deleteDocument(String(id.value));
  goToList();
}

function confirmResetChanges() {
  const extractedClassifications = extractClassifications(document.value);
  initialClassifications.value = { ...extractedClassifications };
  setValues({ ...editData.value, classifications: extractedClassifications });
}

const updateHeadline = () => {
  if (isNew.value) {
    headlineStore.setHeadlines({
      headline: t('pages.documentsDetail.uploadHeadline'),
    });
  } else {
    headlineStore.setHeadlines({
      headline: document.value?.name || t('pages.documentsDetail.viewHeadline'),
    });
  }
};

const hasFilesToUpload = computed(() => {
  return (uploadComponentRef.value?.pendingFiles?.length ?? 0) > 0;
});

const getConfirmationMessage = () => {
  const validTickets = document.value?.tickets.filter(Boolean) ?? [];
  if (validTickets.length > 0) {
    return {
      header: t('confirm.deleteTicketRelation.header'),
      message: t('confirm.deleteTicketRelation.message'),
    };
  }
  return undefined;
};

watchEffect(() => {
  updateHeadline();
});

watch(
  () => route.fullPath,
  () => {
    updateHeadline();
  },
  { immediate: true },
);

const fetchData = async () => {
  const data = await fetchDocument(String(id.value));
  document.value = data && convertDocumentDtoToDocumentSimple(data);
  editData.value = { ...document.value };

  const extractedClassifications = extractClassifications(document.value);
  initialClassifications.value = { ...extractedClassifications };
  setValues({ ...editData.value, classifications: extractedClassifications });
};

const { componentKey } = useDetailView({
  fetchData,
});
</script>

<style scoped lang="css">
@reference "tailwindcss";
@reference "tailwindcss/utilities";
.section-wrapper.new {
  @apply p-0;
}
</style>
